import { useState, useEffect, useMemo, useCallback } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { getCompanies, deleteCompany } from "../../../api/General/Company";
import { CompanyView } from "../../../models/general";
import { copyAndSort } from "../../../utils";
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell
} from "../../../components/ui/Table";
import Button from "../../../components/ui/Button";
import Input from "../../../components/ui/Input";
import Select from "../../../components/ui/Select";
import AppIcon from "../../../components/AppIcon";
import { Spinner } from "../../../components/ui/Spinner";
import Pagination from "../../../components/Pagination";
import { PageLayout } from "../../../components/ui/PageLayout";
import { Building } from "lucide-react";
import SessionRevocationPanel from "./SessionRevocationPanel";

type SortCol = { key: string; desc: boolean } | null;

export default function Companies() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const companyPrimaryColor = useSelector((s: any) => s.user.companyPrimaryColor);

  const [loading, setLoading] = useState(false);
  const [companies, setCompanies] = useState<CompanyView[]>([]);
  // CO-227 — `organizations` / `nonTrialOrganizations` were only ever read to build the org picker
  // inside the deleted "Activate trial company" dialog, and the GetOrganizations() call that fed
  // them went with the state: it was one network round-trip per page load serving a dialog that no
  // longer exists.
  const [showFilters, setShowFilters] = useState(false);
  const [items, setItems] = useState<CompanyView[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const count = 10;

  /*
    CO-227 — the "extend trial" and "activate trial company" dialogs (and their state) are gone.
    Both called Company endpoints that no longer exist. The bucket ORGANISATION they were scoped to
    is deliberately untouched: "Trial Companies Organization" is still the live routing target for
    every self-serve signup, so the filter below stays, misnomer and all. Renaming it is a data
    change, not a string edit.
  */

  const [trialFilterValue, setTrialFilterValue] = useState<string>('nonTrial');
  const [searchText, setSearchText] = useState("");
  const [nameFilter, setNameFilter] = useState("");
  const [countryFilter, setCountryFilter] = useState("");
  const [sort, setSort] = useState<SortCol>(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    const companiesRes = await getCompanies();
    setCompanies(companiesRes.payload);
    setItems(companiesRes.payload);
    setLoading(false);
    setTrialFilterValue('nonTrial');
  };

  const filteredItems = useMemo(() => {
    let result = companies;

    // Trial filter
    if (trialFilterValue === 'nonTrial') {
      result = result.filter(u => !(u.organization?.name ?? '').toLowerCase().includes("trial companies organization"));
    } else if (trialFilterValue === 'trial') {
      result = result.filter(u => (u.organization?.name ?? '').toLowerCase().includes("trial companies organization"));
    }

    // Global search
    if (searchText.trim()) {
      const s = searchText.toLowerCase();
      result = result.filter(u =>
        u.name?.toLowerCase().includes(s) ||
        u.country?.name?.toLowerCase().includes(s) ||
        u.address?.toLowerCase().includes(s) ||
        u.email?.toLowerCase().includes(s) ||
        u.organization?.name?.toLowerCase().includes(s) ||
        u.phoneNumber?.toLowerCase().includes(s) ||
        u.plan?.toLowerCase().includes(s)
      );
    }

    // Name/country filters
    if (nameFilter.trim()) result = result.filter(u => (u.name ?? '').toLowerCase().includes(nameFilter.toLowerCase()));
    if (countryFilter.trim()) result = result.filter(u => (u.country?.name ?? '').toLowerCase().includes(countryFilter.toLowerCase()));

    // Sort
    if (sort) {
      result = copyAndSort(result, sort.key, sort.desc);
    }

    return result;
  }, [companies, trialFilterValue, searchText, nameFilter, countryFilter, sort]);

  const totalPages = Math.ceil(filteredItems.length / count);
  const paginatedItems = filteredItems.slice((currentPage - 1) * count, currentPage * count);

  const handleSort = (key: string) => {
    setSort(prev => {
      if (prev?.key === key) return { key, desc: !prev.desc };
      return { key, desc: false };
    });
  };

  const trialOptions = [
    { value: 'nonTrial', label: t("Normal") },
    { value: 'trial', label: t("Trial") },
    { value: 'all', label: t("All") },
  ];


  const columns = [
    { key: "name", name: t("Name") },
    { key: "country", name: t("Country") },
    { key: "phonenumber", name: t("Phone Number") },
    { key: "status", name: t("Status") },
    { key: "options", name: t("Options") },
  ];

  const SortHeader = ({ col }: { col: typeof columns[0] }) => (
    <button
      className="inline-flex items-center gap-1 text-xs font-medium uppercase text-muted-foreground hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded"
      onClick={() => handleSort(col.key)}
    >
      {col.name}
      {sort?.key === col.key && (
        <AppIcon name={sort.desc ? 'ChevronDown' : 'ChevronUp'} size={14} />
      )}
    </button>
  );

  return (
    <PageLayout
      title={t("Companies")}
      icon={Building}
      accentColor="slate"
      headerActions={(
        <Button
          variant="outline"
          iconName="Plus"
          onClick={() => navigate("/settings/companies/add")}
        >
          {t("Add Company")}
        </Button>
      )}
    >
      <div className="flex flex-col overflow-x-auto">
      {/* Toolbar */}
      <div className="flex items-center justify-between gap-4 border-b border-border px-4 py-3">
        <div className="flex items-center gap-2">
          <Select
            options={trialOptions}
            value={trialFilterValue}
            onChange={(val: any) => { setTrialFilterValue(val); setCurrentPage(1); }}
            className="w-[140px]"
          />
          <div className="relative">
            <AppIcon name="Search" size={16} className="absolute start-2.5 top-2.5 text-muted-foreground" />
            <Input
              placeholder={t("Search")}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => { setSearchText(e.target.value); setCurrentPage(1); }}
              className="ps-8 flex-1"
            />
          </div>
        </div>
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="icon" title={t("Filters")} onClick={() => setShowFilters(f => !f)}>
            <AppIcon name="ListFilter" size={18} />
          </Button>
          <Button variant="ghost" size="icon" title={t("Add Company")} onClick={() => navigate("/settings/companies/add")}>
            <AppIcon name="Plus" size={18} />
          </Button>
        </div>
      </div>

      <div className="flex-1">
        <div className="flex flex-col">
          {showFilters && (
            <div className="flex gap-4 p-4">
              <Input placeholder={t("Name")} onChange={(e: React.ChangeEvent<HTMLInputElement>) => { setNameFilter(e.target.value); setCurrentPage(1); }} />
              <Input placeholder={t("Country")} onChange={(e: React.ChangeEvent<HTMLInputElement>) => { setCountryFilter(e.target.value); setCurrentPage(1); }} />
            </div>
          )}

          <div className="rounded-lg border border-border bg-card">
            {loading ? (
              <div className="flex justify-center p-6">
                <Spinner size="lg" />
              </div>
            ) : (
              <Table stickyHeader>
                <TableHeader>
                  <TableRow>
                    {columns.map((col) => (
                      <TableHead key={col.key}>
                        {col.key !== 'options' ? <SortHeader col={col} /> : col.name}
                      </TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paginatedItems.map((item, index) => (
                    <TableRow key={index}>
                      <TableCell>{item.name}</TableCell>
                      <TableCell>{item.country?.name}</TableCell>
                      <TableCell>{item.phoneNumber}</TableCell>
                      {/* CO-227 — the "N days left" suffix is gone; trialDays is now always 0. */}
                      <TableCell>{t(item.status)}</TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button variant="outline" size="icon" title={t("Details")} asChild>
                            <Link to={`/settings/companies/${item.id}`}><AppIcon name="MoreHorizontal" size={16} /></Link>
                          </Button>
                          {/* Employee roster / batch-add shortcuts stay removed: those are company
                              employee + HR surfaces and Operator has no access to them (CLAUDE.md Role
                              Model). Leave CONFIGURATION is different — the 2026-07-29 owner carve-out
                              keeps it with Operator, so it links to the config-only page (leave types +
                              per-country rules), NOT to the HR-only /leave-policies. */}
                          <Button variant="outline" size="icon" title={t("Leave Configuration")} asChild>
                            <Link
                              to={`/settings/operator/leave-configuration?companyId=${item.id}`}
                              aria-label={`${t("Leave Configuration")} - ${item.name}`}
                            >
                              <AppIcon name="Palmtree" size={16} />
                            </Link>
                          </Button>
                          <Button variant="outline" size="icon" title={t("Company Rules")} asChild>
                            <Link to={`/settings/operator/company-rules?companyId=${item.id}`}><AppIcon name="ScrollText" size={16} /></Link>
                          </Button>
                          <Button
                            variant="outline"
                            size="icon"
                            title={t("Archive")}
                            onClick={async () => {
                              if (window.confirm(t("Are you sure you want to delete this company?"))) {
                                await deleteCompany(item.id);
                                fetchData();
                              }
                            }}
                          >
                            <AppIcon name="Trash2" size={16} />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>

          <div className="p-4">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
            />
          </div>
        </div>
      </div>
      </div>
      {/* CO-708 — platform-wide "sign out everyone" (Operator-only route; server keeps the caller's session). */}
      <div className="p-4">
        <SessionRevocationPanel scope="platform" />
      </div>
    </PageLayout>
  );
}
